import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { SentencePractice } from './practice/SentencePractice';
import { WordPractice } from './practice/WordPractice';
import { AudioControls } from './practice/AudioControls';
import { UnknownWordsDisplay } from './practice/UnknownWordsDisplay';
import { type Sentence, type Word } from '../types';
import { splitIntoSentences, extractNormalizedWords, extractWords } from '../utils/textProcessing';
import { supabase, getLessonsTable, getWordsTable, getUnknownWordsTable, getProgressTable } from '../utils/supabase';
import { playSuccessSound } from '../utils/audio';
import { useAudioPlayer } from '../hooks/useAudioPlayer';
import { useSpeechRecognition } from '../hooks/useSpeechRecognition';
import { LessonCompletionPopup } from './LessonCompletionPopup';
import { useTranslation } from '../utils/translations';
import { ReportIssueModal } from './ReportIssueModal';
import { Flag } from 'lucide-react';

function PracticePage() {
  const { lessonId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [sentences, setSentences] = useState<Sentence[]>([]);
  const [currentSentenceIndex, setCurrentSentenceIndex] = useState(0);
  const [showText, setShowText] = useState(true);
  const [shouldShowQuiz, setShouldShowQuiz] = useState(true);
  const cleanupTimeoutRef = useRef<number | null>(null);
  const [lessonTranslation, setLessonTranslation] = useState<string>('');
  const [unknownWords, setUnknownWords] = useState<string[]>([]);
  const [canSkip, setCanSkip] = useState(false);
  const [showWordCards, setShowWordCards] = useState(true);
  const [showWordQuiz, setShowWordQuiz] = useState(true);
  const [wordBankData, setWordBankData] = useState<
    Array<{
      word: string;
      pronunciation?: string;
      translation: string;
      persian_translation?: string;
      examples: Array<{
        dutch: string;
        english: string;
        persian?: string;
      }>;
    }>
  >([]);
  const [matchPercentage, setMatchPercentage] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [practiceMode, setPracticeMode] = useState<'sentence' | 'word'>('sentence');
  const [lessonWords, setLessonWords] = useState<Word[]>([]);
  const [lessonText, setLessonText] = useState<string>('');
  const [showFinalText, setShowFinalText] = useState(false);
  const [hasFinalTextPlayed, setHasFinalTextPlayed] = useState(false);
  const [isReviewLesson, setIsReviewLesson] = useState(false);
  const [isReadingFinalText, setIsReadingFinalText] = useState<boolean>(false);
  const [currentSegmentText, setCurrentSegmentText] = useState<string>('');
  const [currentSegmentTranslation, setCurrentSegmentTranslation] = useState<string>('');
  const finalTextTimeoutRef = useRef<number | null>(null);
  const [sentenceCompleted, setSentenceCompleted] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [nativeLanguage, setNativeLanguage] = useState<string>('English');
  const { t } = useTranslation(nativeLanguage);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [audioSegments, setAudioSegments] = useState<
    Array<{
      segment_text: string;
      start_time: number;
      end_time: number;
      audio_url?: string;
    }>
  >([]);
  const mountedRef = useRef(true);
  const [currentSegment, setCurrentSegment] = useState<{
    text: string;
    start: number;
    end: number;
    audio_url?: string;
  } | null>(null);
  const [audioReadyAt, setAudioReadyAt] = useState<number>(Date.now());
  const [currentSegmentIndex, setCurrentSegmentIndex] = useState(0);
  const [showTranslation, setShowTranslation] = useState(false);
  const segmentRefs = useRef<(HTMLDivElement | null)[]>([]);
  const navigationRef = useRef<boolean>(false);
  const practiceTimeoutRef = useRef<number | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const scrollTimeoutRef = useRef<number | null>(null);
  const loadingTimeoutRef = useRef<number | null>(null);
  const [wordBankIndex, setWordBankIndex] = useState(0);
  const [micPermissionGranted, setMicPermissionGranted] = useState(false);
  const [spokenText, setSpokenText] = useState('');
  const [spokenWords, setSpokenWords] = useState<string[]>([]);
  const errorTimeoutRef = useRef<number | null>(null);
  const [pageLoadTime] = useState(Date.now());
  const [loadingError, setLoadingError] = useState<string | null>(null);
  const loadingRef = useRef(loading);
  const activeSentenceIndexRef = useRef(currentSentenceIndex);

  const handleSpeechResult = (text: string, words: string[], isFinal: boolean) => {
    // Check if we're navigating or if this result is for a different sentence
    if (navigationRef.current) {
      console.log('Ignoring speech result during navigation');
      return;
    }

    // Ignore stale results from previous sentences
    if (activeSentenceIndexRef.current !== currentSentenceIndex) {
      console.log('Ignoring stale speech result from sentence', activeSentenceIndexRef.current, 'current is', currentSentenceIndex);
      return;
    }

    mountedRef.current = true;

    if (!currentSentence) return;

    // Clean target sentence from ALL punctuation and special characters
    const targetWords = extractNormalizedWords(currentSentence.dutch);

    let matchedWords = 0;
    // Clean spoken words from ALL punctuation and special characters
    const cleanedWords = extractNormalizedWords(words.join(' '));

    cleanedWords.forEach(word => {
      if (targetWords.includes(word)) matchedWords++;
    });
    const percentage = Math.min((matchedWords / targetWords.length) * 100, 100);

    // Only update state if we're still on the same sentence and not navigating
    if (!navigationRef.current && activeSentenceIndexRef.current === currentSentenceIndex) {
      setMatchPercentage(percentage);
      setSpokenText(text);
      setSpokenWords(words);

      // Store sentence practice stats when user achieves a new max percentage
      if (percentage > matchPercentage && lessonId) {
        saveSentencePracticeStats(percentage);
      }

      // Handle successful completion (75% or higher)
      // Allow activation on both final and non-final results for 100% match
      // This ensures immediate feedback when user speaks perfectly
      if ((isFinal || percentage >= 100) && percentage >= 75 && !sentenceCompleted) {
        // Stop recording and mark as completed
        // Don't call stopRecording() here - it will be handled by the SentencePractice component
        console.log('Speech recognition: sentence completed with', percentage, '% match', isFinal ? '(final)' : '(interim)');
        setSentenceCompleted(true);
        setCanSkip(false);
        setAttempts(0);

        // Note: We're removing the automatic navigation here
        // This fixes the issue where the Next button briefly activates then deactivates
        // Now the user will need to click the Next button manually
      } else if (isFinal && cleanedWords.length >= targetWords.length && !sentenceCompleted) {
        const newAttempts = attempts + 1;
        setAttempts(newAttempts);
        if (newAttempts >= 3) setCanSkip(true);
      }
    } else {
      console.log('Skipping state update - navigation in progress or sentence changed');
    }
  };

  const saveSentencePracticeStats = async (percentage: number) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || !lessonId) return;

      // Upsert the stats - update if exists, insert if not
      const { error } = await supabase
        .from('sentence_practice_stats')
        .upsert({
          user_id: user.id,
          lesson_id: lessonId,
          sentence_index: currentSentenceIndex,
          max_match_percentage: Math.round(percentage),
          attempts: attempts + 1,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_id,lesson_id,sentence_index',
          ignoreDuplicates: false
        });

      if (error) {
        console.error('Error saving sentence practice stats:', error);
      } else {
        console.log(`Saved sentence ${currentSentenceIndex} stats: ${Math.round(percentage)}%`);
      }
    } catch (error) {
      console.error('Error in saveSentencePracticeStats:', error);
    }
  };

  const handleWordCardComplete = async () => {
    // Update word bank data first
    setWordBankData(prevData => prevData.slice(1));
    
    // Check if this was the last word card
    if (wordBankData.length <= 1 && shouldShowQuiz) {
      setShowWordCards(false);
      setShowWordQuiz(true);
    }
  };

  // Update loadingRef whenever loading state changes
  useEffect(() => {
    loadingRef.current = loading;
  }, [loading]);

  const [isCompleting, setIsCompleting] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [showCompletionPopup, setShowCompletionPopup] = useState(false);
  const [completedLessonTitle, setCompletedLessonTitle] = useState('');
  const [user, setUser] = useState<{ uid: string; currentLevel: LanguageLevel } | null>(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [lessonTitle, setLessonTitle] = useState<string>('');
  const [surveyData, setSurveyData] = useState<{ initialUnderstanding: number | null; surveyId: string | null }>({
    initialUnderstanding: null,
    surveyId: null
  });

  // Confetti effect function

  const { isPlaying, currentTime, duration, play: playAudio, pause: pauseAudio, cleanup: cleanupAudio } =
    useAudioPlayer(audioUrl);

  const segmentAudioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlayingSegment, setIsPlayingSegment] = useState(false);
  const [segmentCurrentTime, setSegmentCurrentTime] = useState(0);
  const [segmentDuration, setSegmentDuration] = useState(0);
  const [segmentDurations, setSegmentDurations] = useState<number[]>([]);
  const segmentHasPlayedRef = useRef(false);
  const currentSegmentIndexRef = useRef(0);
  const audioSegmentsRef = useRef(audioSegments);
  const isAutoAdvancingRef = useRef(false);

  useEffect(() => { currentSegmentIndexRef.current = currentSegmentIndex; }, [currentSegmentIndex]);
  useEffect(() => { audioSegmentsRef.current = audioSegments; }, [audioSegments]);

  // Reset per-segment time tracking when segment index changes
  useEffect(() => {
    setSegmentCurrentTime(0);
    setSegmentDuration(0);
  }, [currentSegmentIndex]);

  // Pre-load all segment durations for AI lessons so the total duration is known upfront
  useEffect(() => {
    const aiSegments = audioSegments.filter(s => s.audio_url);
    if (aiSegments.length === 0) return;
    aiSegments.forEach((segment, i) => {
      const idx = audioSegments.indexOf(segment);
      const probe = new Audio(segment.audio_url!);
      probe.preload = 'metadata';
      probe.onloadedmetadata = () => {
        setSegmentDurations(prev => {
          const updated = [...prev];
          updated[idx] = probe.duration;
          return updated;
        });
      };
      probe.load();
    });
  }, [audioSegments]);

  const advanceToNextSegment = () => {
    const idx = currentSegmentIndexRef.current;
    const segments = audioSegmentsRef.current;
    if (idx < segments.length - 1) {
      isAutoAdvancingRef.current = true;
      setCurrentSegmentIndex(idx + 1);
    }
  };

  // Helper function to play audio for a segment (handles both traditional and AI-generated lessons)
  const playSegmentAudio = (segment: typeof audioSegments[0]) => {
    isAutoAdvancingRef.current = false;
    wasPlayingRef.current = false;
    if (segment.audio_url) {
      // AI-generated lesson with individual audio URLs
      console.log('Playing individual audio URL:', segment.audio_url);

      // Clean up previous audio if exists
      if (segmentAudioRef.current) {
        segmentAudioRef.current.ontimeupdate = null;
        segmentAudioRef.current.onloadedmetadata = null;
        segmentAudioRef.current.pause();
        segmentAudioRef.current.src = '';
      }

      setSegmentCurrentTime(0);
      setSegmentDuration(0);

      const audio = document.createElement('audio');
      audio.setAttribute('playsinline', '');
      audio.setAttribute('webkit-playsinline', '');
      audio.src = segment.audio_url;
      segmentAudioRef.current = audio;

      audio.onloadedmetadata = () => {
        if (mountedRef.current) {
          setSegmentDuration(audio.duration);
          const idx = currentSegmentIndexRef.current;
          setSegmentDurations(prev => {
            const updated = [...prev];
            updated[idx] = audio.duration;
            return updated;
          });
        }
      };

      audio.ontimeupdate = () => {
        if (mountedRef.current) setSegmentCurrentTime(audio.currentTime);
      };

      audio.onended = () => {
        console.log('Segment audio playback ended');
        if (mountedRef.current) {
          setIsPlayingSegment(false);
          setSegmentCurrentTime(audio.duration);
        }
        setTimeout(advanceToNextSegment, 500);
      };

      audio.onerror = (error) => {
        console.error('Error playing segment audio:', error);
        if (mountedRef.current) setIsPlayingSegment(false);
      };

      audio.play()
        .then(() => {
          segmentHasPlayedRef.current = true;
          if (mountedRef.current) setIsPlayingSegment(true);
        })
        .catch((error) => {
          console.error('Failed to play segment audio:', error);
          if (mountedRef.current) setIsPlayingSegment(false);
        });
    } else {
      // Traditional lesson with timestamps
      console.log('Playing timestamp-based audio:', segment.start_time, segment.end_time);
      segmentHasPlayedRef.current = true;
      playAudio(segment.start_time, segment.end_time);
    }
  };

  // Helper function to pause segment audio
  const pauseSegmentAudio = () => {
    wasPlayingRef.current = false;
    if (segmentAudioRef.current) {
      segmentAudioRef.current.pause();
      setIsPlayingSegment(false);
      if (mountedRef.current) setSegmentCurrentTime(segmentAudioRef.current.currentTime);
    } else {
      pauseAudio();
    }
  };

  // Check if currently playing (either traditional or segment audio)
  const isCurrentlyPlaying = isPlaying || isPlayingSegment;

  // Destructuring the hook with all returned properties, including isInitializing
  const {
    isRecording,
    isInitializing,
    spokenText: recognizedText,
    spokenWords: recognizedWords,
    startRecording,
    stopRecording,
    cleanup: cleanupSpeech,
    acquireStream,
    releaseStream
  } = useSpeechRecognition(handleSpeechResult);

  // Debug log component state
  useEffect(() => {
    const loadTime = Date.now() - pageLoadTime;
    console.log(`PracticePage state update (${loadTime}ms):`, { 
      isRecording, 
      isInitializing, 
      sentenceCompleted,
      loading,
      practiceMode
    });
  }, [isRecording, isInitializing, sentenceCompleted, loading, practiceMode, pageLoadTime]);

  // Removed duplicate audio playback effect - handled by useEffect at line 876

  // Handle auto-scrolling when segment changes
  useEffect(() => {
    if (showFinalText && segmentRefs.current[currentSegmentIndex] && scrollContainerRef.current) {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      scrollTimeoutRef.current = window.setTimeout(() => {
        const container = scrollContainerRef.current;
        const element = segmentRefs.current[currentSegmentIndex];

        if (!container || !element) return;

        const containerRect = container.getBoundingClientRect();
        const elementRect = element.getBoundingClientRect();

        const elementCenterRelativeToContainer =
          elementRect.top - containerRect.top + container.scrollTop + elementRect.height / 2;

        const targetScrollTop = elementCenterRelativeToContainer - containerRect.height / 2;

        container.scrollTo({
          top: Math.max(0, targetScrollTop),
          behavior: 'smooth'
        });
      }, 150);
    }

    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [currentSegmentIndex, showFinalText]);

  useEffect(() => {
    console.log('Setting current segment for sentence index:', currentSentenceIndex, 'total segments:', audioSegments.length);
    if (audioSegments[currentSentenceIndex]) {
      const segment = {
        text: audioSegments[currentSentenceIndex].segment_text,
        start: audioSegments[currentSentenceIndex].start_time,
        end: audioSegments[currentSentenceIndex].end_time,
        audio_url: audioSegments[currentSentenceIndex].audio_url,
      };
      console.log('Setting segment:', segment);
      setCurrentSegment(segment);
    } else {
      console.log('No audio segment found for sentence index:', currentSentenceIndex);
      setCurrentSegment(null);
    }
  }, [currentSentenceIndex, audioSegments]);

  const handleWordClick = async (word: string) => {
    if (isReviewLesson) return;
    // Clean the word from punctuation before saving as unknown
    const cleanWord = word.toLowerCase().replace(/[.,!?؟;:()'"\-]/g, '').trim(); 
    console.log('Marking word as unknown:', cleanWord);
    
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return;
      
      // Get the correct tables based on native language
      const lessonsTable = getLessonsTable();
      const unknownWordsTable = getUnknownWordsTable();
      
      // First verify the lesson exists and get its level
      const { data: lesson, error: lessonError } = await supabase
        .from(lessonsTable)
        .select('id, level')
        .eq('id', lessonId)
        .single();

      if (lessonError) {
        console.error('Error finding lesson:', lessonError);
        return;
      }

      if (!lesson) {
        console.error('Lesson not found in table:', lessonsTable);
        return;
      }

      // Check if word is already marked as unknown
      const { data: existingWord } = await supabase
        .from(unknownWordsTable)
        .select('id')
        .eq('user_id', user.id)
        .eq('lesson_id', lessonId!)
        .eq('word', cleanWord)
        .eq('level', lesson.level)
        .maybeSingle();

      // If word is not already marked as unknown, add it
      if (!existingWord) {
        const { error } = await supabase
          .from(unknownWordsTable)
          .insert({
            user_id: user.id,
            lesson_id: lessonId!,
            word: cleanWord,
            level: lesson.level || 'A1' // Default to A1 if level is missing
          });

        if (error) {
          console.error('Error saving unknown word:', error);
          return;
        }

        setUnknownWords([...unknownWords, cleanWord]);
      }
    } catch (error) {
      // Only log the error, don't show it to the user
      console.error('Error saving unknown word:', 
        error instanceof Error ? error.message : error);
    }
  };

  const currentSentence = sentences[currentSentenceIndex];

  useEffect(() => {
    const checkAdmin = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('is_admin, native_language')
          .eq('user_id', user.id)
          .single();
        setIsAdmin(profile?.is_admin || false);
        setNativeLanguage(profile?.native_language || 'English');
      }
    };
    setIsMobile('ontouchstart' in window || navigator.maxTouchPoints > 0);
    checkAdmin();
    
    // Set mount reference
    mountedRef.current = true;
    navigationRef.current = false;

    console.log('PracticePage mounted at:', new Date().toISOString());

    // Set a timeout to prevent infinite loading state
    loadingTimeoutRef.current = window.setTimeout(() => {
      if (mountedRef.current && loadingRef.current) {
        console.log('Loading timeout reached, forcing loading state to false', new Date().toISOString());
        setLoading(false);
        setLoadingError('Loading timeout reached. Please try refreshing the page.');
      }
    }, 8000); // 8 second timeout
    
    // Also set a timeout to show an error if we're still loading after 10 seconds
    errorTimeoutRef.current = window.setTimeout(() => {
      if (mountedRef.current && loadingRef.current) {
        console.error('Practice page still loading after 10 seconds - something is wrong');
        setLoadingError('Loading is taking longer than expected. You may try refreshing the page.');
      }
    }, 10000);
    
    return () => {
      mountedRef.current = false;
      navigationRef.current = true;

      // Force cleanup of all speech recognition and audio resources
      console.log('PracticePage unmounting - ensuring all resources are cleaned up');

      // Stop recognition and release the shared microphone stream
      cleanupSpeech();
      releaseStream();

      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
        errorTimeoutRef.current = null;
      }

      console.log('PracticePage unmounting, cleaning up resources');
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
        loadingTimeoutRef.current = null;
      }

      cleanupAudio();

      // Clean up segment audio
      if (segmentAudioRef.current) {
        segmentAudioRef.current.pause();
        segmentAudioRef.current.src = '';
        segmentAudioRef.current = null;
      }

      if (finalTextTimeoutRef.current) window.clearTimeout(finalTextTimeoutRef.current);
      if (scrollTimeoutRef.current) window.clearTimeout(scrollTimeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Ensure cleanup when navigating away or unmounting
  // Keep the active sentence index ref in sync with the state
  useEffect(() => {
    activeSentenceIndexRef.current = currentSentenceIndex;
  }, [currentSentenceIndex]);

  // Ensure speech recognition is cleaned up when not in sentence practice mode
  useEffect(() => {
    if (practiceMode !== 'sentence' || showFinalText) {
      console.log('Not in sentence practice mode, ensuring speech recognition is stopped');
      if (isRecording) {
        stopRecording();
      }
      cleanupSpeech();
    }
  }, [practiceMode, showFinalText, isRecording, stopRecording, cleanupSpeech]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      console.log('Page is being unloaded, cleaning up resources');
      cleanupSpeech();
      cleanupAudio();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [cleanupSpeech, cleanupAudio]);

  // Load survey data whenever lessonId changes
  useEffect(() => {
    const loadSurveyData = async () => {
      if (!lessonId) return;

      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: survey } = await supabase
          .from('lesson_understanding_surveys')
          .select('id, initial_understanding')
          .eq('user_id', user.id)
          .eq('lesson_id', lessonId)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (survey) {
          console.log('Survey data loaded:', survey);
          setSurveyData({
            surveyId: survey.id,
            initialUnderstanding: survey.initial_understanding
          });
        } else {
          console.log('No survey found for this lesson');
          setSurveyData({
            surveyId: null,
            initialUnderstanding: null
          });
        }
      }
    };

    loadSurveyData();
  }, [lessonId]);

  useEffect(() => {
    if (!lessonId) return;
    const skipToFinalText = location.state?.skipToFinalText;
    console.log('Loading practice page for lesson:', lessonId, 'skipToFinalText:', skipToFinalText);

    if (!lessonId) {
      console.error('No lesson ID provided');
      setLoadingError('No lesson ID provided');
      setLoading(false);
      return;
    }
    
    // If skipToFinalText is true, skip directly to final text
    if (skipToFinalText) {
      setShowFinalText(true);
      setCurrentSegmentIndex(0); // Reset to first segment
    }
    
    setCanSkip(false);
    setAttempts(0);
    setSentenceCompleted(false);
    
    const loadLesson = async () => {
      try {
        if (mountedRef.current) {
        
        // Get user data
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          const { data: profile } = await supabase
            .from('profiles')
            .select('current_level')
            .eq('user_id', session.user.id)
            .maybeSingle();
          
          setUser({
            uid: session.user.id,
            currentLevel: (profile?.current_level as LanguageLevel) || 'A1'
          });
        }
        
          setLoading(true);
          setLoadingError(null);
        }
        console.log('Loading lesson content...');
        
        const table = getLessonsTable();
        const wordsTable = getWordsTable();
        
        // First, get lesson details without words join
        const { data: lesson, error: lessonError } = await supabase
          .from(table)
          .select(`
            id,
            level,
            title,
            description,
            text,
            text_translation,
            category,
            difficulty,
            is_review,
            show_text,
            audio_url,
            segments_timepoints,
            order_index,
            generated_for
          `)
          .eq('id', lessonId)
          .single();

        if (lessonError) {
          console.error('Error loading lesson:', lessonError);
          throw lessonError;
        }

        if (!lesson) {
          console.error('Lesson not found with ID:', lessonId);
          throw new Error(`Lesson not found with ID: ${lessonId}`);
        }
        
        // Separately fetch words using the correct table
        const { data: wordsData, error: wordsError } = await supabase
          .from(wordsTable)
          .select('*')
          .eq('lesson_id', lessonId);

        if (wordsError) {
          console.error('Error loading words:', wordsError);
          // Don't throw here, just log and continue without words
        }
        
        console.log('Lesson loaded successfully:', lesson.title);

        // Only update state if component is still mounted
        if (!mountedRef.current) return;

        setIsReviewLesson(lesson.is_review || false);
        setLessonWords(wordsData || []);
        setAudioUrl(lesson.audio_url || null);
        setCompletedLessonTitle(lesson.title || '');
        setLessonTitle(lesson.title || '');
        
        // Map segments_timepoints to audio segments with validation
        // Support both traditional lessons (with start/end times) and AI-generated lessons (with audio_url)
        const segments = (lesson.segments_timepoints || [])
          .filter((timepoint: any) => (
            timepoint &&
            typeof timepoint.text === 'string' &&
            (
              // Traditional lesson with timestamps
              (typeof timepoint.start === 'number' &&
               typeof timepoint.end === 'number' &&
               timepoint.start >= 0 &&
               timepoint.end > timepoint.start) ||
              // AI-generated lesson with audio_url
              typeof timepoint.audio_url === 'string'
            )
          ))
          .map((timepoint: any) => ({
            segment_text: timepoint.text,
            start_time: timepoint.start || 0,
            end_time: timepoint.end || 0,
            audio_url: timepoint.audio_url,
          }));

        setAudioSegments(segments);
        
        // Set initial segment if available
        if (segments.length > 0) {
          setCurrentSegment({
            text: segments[0].segment_text,
            start: segments[0].start_time,
            end: segments[0].end_time,
            audio_url: segments[0].audio_url,
          });
          if (skipToFinalText) {
            setCurrentSegmentIndex(0);
            playSegmentAudio(segments[0]);
          }
        }

        if (lesson.is_review) {
          console.log('This is a review lesson');
          setShowText(lesson.show_text !== false); // Only affects first page
          setPracticeMode('word');
          setLessonText(lesson.text || '');
          setShowWordQuiz(false);
          setLessonTranslation(lesson.text_translation || '');
          if (lesson.words?.length > 0) setUnknownWords(lesson.words.map(word => word.dutch));
        } else if (lesson.text && lesson.text_translation) {
          console.log('This is a regular lesson with text');
          setShowText(lesson.show_text !== false); // Only affects first page
          const rawSentences = splitIntoSentences(lesson.text);
          const translatedSentences = splitIntoSentences(lesson.text_translation);
          setLessonText(lesson.text);
          setLessonTranslation(lesson.text_translation);
          const processedSentences = rawSentences.map((dutch, index) => ({
            dutch,
            english: translatedSentences[index] || 'Translation not available',
            words: extractWords(dutch),
          }));
          setSentences(processedSentences);
        } else {
          console.log('Lesson content is missing text or translation');
          throw new Error('Lesson content is missing');
        }
      } catch (error) {
        console.error('Error loading lesson:', error);
        if (mountedRef.current) {
          setLoadingError(error instanceof Error ? error.message : 'Failed to load lesson content');
        }
        navigate('/lessons');
      } finally {
        if (mountedRef.current) {
          console.log('Setting loading to false');
          setLoading(false);
        }
        
        if (loadingTimeoutRef.current) {
          clearTimeout(loadingTimeoutRef.current);
          loadingTimeoutRef.current = null;
        }
      }
    };
    
    loadLesson();
  }, [lessonId, navigate]);

  const markLessonCompleted = async (shouldNavigate: boolean = false) => {
    try {
      console.log('Marking lesson as completed...');

      // Set flag first to prevent further recording attempts
      navigationRef.current = true;

      // Stop any active recordings - this calls cleanup internally
      stopRecording();

      // Wait for cleanup to fully complete - longer delay for mobile devices
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || !lessonId) {
        console.error('No user or lessonId found for marking completion');
        return;
      }

      // Get correct table name using the helper function
      const progressTable = getProgressTable();
      console.log('Using progress table:', progressTable);

      // Log the user and lesson IDs for debugging
      console.log('Lesson ID:', lessonId);
      console.log('User ID:', user.id);

      // Upsert the progress record
      const { data, error } = await supabase
        .from(progressTable)
        .upsert({
          user_id: user.id,
          lesson_id: lessonId,
          completed: true,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_id,lesson_id',
          ignoreDuplicates: false
        });

      if (error) {
        console.error('Error marking lesson as completed:', error);
        throw error;
      }
      
      console.log('Lesson marked as completed successfully:', data);
      
      // Stop recording when returning to lessons
      stopRecording();
      
      // Set flag to prevent further recording attempts during navigation
      navigationRef.current = true;
      
      cleanupSpeech();
      console.log('Navigating away from lesson - speech recognition cleaned up');
      
      if (shouldNavigate) navigate('/lessons');
    } catch (error) {
      console.error('Error marking lesson as completed:', error);
    }
  };

  const handleSentencePracticeComplete = async () => {
    try {
      // Set flag to prevent further recording attempts
      navigationRef.current = true;

      // Stop any recording - this calls cleanup internally
      console.log('Sentence practice complete - stopping recording');
      stopRecording();

      // Wait for cleanup to fully complete
      await new Promise(resolve => setTimeout(resolve, 500));

      console.log('Moving to word practice - speech recognition stopped and cleaned up');

      // Mark as completed after cleanup
      await markLessonCompleted();

      if (unknownWords.length > 0) {
        const { data: wordBankEntries, error } = await supabase
          .from('word_bank')
          .select('*')
          .in('dutch', unknownWords.map(w => w.toLowerCase().replace(/[.,!?;:()'"\-]/g, '').trim()))
          .order('created_at', { ascending: true });

        if (error) throw error;

        const processedData = wordBankEntries.map(entry => ({
          word: entry.dutch,
          pronunciation: entry.pronunciation,
          translation: entry.translation,
          persian_translation: entry.persian_translation,
          examples: [
            entry.example1 && (entry.example1_translation || entry.example1_persian_translation)
              ? { 
                  dutch: entry.example1, 
                  english: entry.example1_translation,
                  persian: entry.example1_persian_translation 
                }
              : null,
            entry.example2 && (entry.example2_translation || entry.example2_persian_translation)
              ? { 
                  dutch: entry.example2, 
                  english: entry.example2_translation,
                  persian: entry.example2_persian_translation 
                }
              : null,
            entry.example3 && (entry.example3_translation || entry.example3_persian_translation)
              ? { 
                  dutch: entry.example3, 
                  english: entry.example3_translation,
                  persian: entry.example3_persian_translation 
                }
              : null,
          ].filter(Boolean),
        }));
        
        // Only proceed to word practice if we have word data
        if (processedData.length > 0) {
          console.log('Starting word practice with data:', processedData);
          setWordBankData(processedData);
          setPracticeMode('word');
          setShowWordCards(true); // Start with cards
          setShowWordQuiz(true); // Enable quiz after cards
          setShouldShowQuiz(true);
        } else {
          // If no word data, proceed to final text
          console.log('No word bank data, proceeding to final text');
          setShowFinalText(true);
          setIsReadingFinalText(true);

          if (audioSegments.length > 0) {
            setCurrentSegmentIndex(0);
            playSegmentAudio(audioSegments[0]);
          }
        }
      } else {
        // No unknown words, proceed directly to final text
        console.log('No unknown words, proceeding to final text');
        setShowFinalText(true);
        setIsReadingFinalText(true);

        if (audioSegments.length > 0) {
          setCurrentSegmentIndex(0);
          playSegmentAudio(audioSegments[0]);
        }
      }

      // Reset navigation flag after all transitions are set
      navigationRef.current = false;
    } catch (error) {
      console.error('Error completing lesson:', error);
    }
  };

  const handleNext = async () => {
    await proceedToNext();
  }; 

  const proceedToNext = async () => {
    // First check if we're navigating away to prevent starting new recording
    if (navigationRef.current) {
      console.log('Ignoring sentence completion during navigation');
      return;
    }

    // Set flag to prevent activation commands from affecting the next page
    setSentenceCompleted(false);

    // Set navigation flag to prevent any state updates during navigation
    navigationRef.current = true;

    pauseAudio();

    // Stop recognition first (synchronous, aborts the recognition engine)
    stopRecording();

    // Clean up all timeouts first
    if (cleanupTimeoutRef.current) {
      window.clearTimeout(cleanupTimeoutRef.current);
      cleanupTimeoutRef.current = null;
    }

    // Await actual hardware release — resolves only when mic tracks have fired
    // 'ended' (or after a 600ms safety fallback). Then enforce a minimum quiet
    // period so iOS fully exits PlayAndRecord mode before the next audio plays.
    console.log('Moving to next sentence - awaiting stream release');
    const releaseStart = Date.now();
    await releaseStream();
    // Guarantee at least 700ms total has passed since we started releasing,
    // regardless of how quickly the tracks fired 'ended'. This covers the case
    // where the stream was already released by the sentenceCompleted effect
    // (in which case releaseStream resolves instantly).
    const isMobile = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    if (isMobile) {
      const elapsed = Date.now() - releaseStart;
      const remaining = 1500 - elapsed;
      if (remaining > 0) await new Promise(r => setTimeout(r, remaining));
    }
    const streamReleasedAt = Date.now();
    setAudioReadyAt(streamReleasedAt);
    console.log('Stream fully released, proceeding to next sentence');

    setSpokenText('');
    setSpokenWords([]);
    setSentenceCompleted(false);
    setCanSkip(false);
    setMatchPercentage(0);

    if (currentSentenceIndex < sentences.length - 1) {
      setAttempts(0);
      setShowTranslation(false);
      const nextIndex = currentSentenceIndex + 1;

      // Update the active sentence index ref BEFORE changing the actual index
      activeSentenceIndexRef.current = nextIndex;

      setCurrentSentenceIndex(nextIndex);

      // Reset navigation flag after state is updated
      navigationRef.current = false;

      const nextSegmentIndex = nextIndex;
      if (audioSegments[nextSegmentIndex]) {
        setCurrentSegment({
          text: audioSegments[nextSegmentIndex].segment_text,
          start: audioSegments[nextSegmentIndex].start_time,
          end: audioSegments[nextSegmentIndex].end_time,
          audio_url: audioSegments[nextSegmentIndex].audio_url,
        });
      }
    } else {
      await handleSentencePracticeComplete();
      navigationRef.current = false;
    }
  };

  // For traditional (timestamp-based) lessons: advance when isPlaying transitions true→false
  const wasPlayingRef = useRef(false);
  useEffect(() => {
    if (!showFinalText) return;
    const isTraditional = !audioSegmentsRef.current[currentSegmentIndexRef.current]?.audio_url;
    if (!isTraditional) return;

    if (isPlaying) {
      wasPlayingRef.current = true;
    } else if (wasPlayingRef.current) {
      wasPlayingRef.current = false;
      const timeoutId = window.setTimeout(advanceToNextSegment, 500);
      return () => clearTimeout(timeoutId);
    }
  }, [isPlaying, showFinalText]);

  // Handle playing audio when segment changes
  useEffect(() => {
    console.log(`🔍 Segment change effect triggered. Index: ${currentSegmentIndex}, Total segments: ${audioSegments.length}, showFinalText: ${showFinalText}`);

    if (showFinalText && currentSegmentIndex < audioSegments.length) {
      const segment = audioSegments[currentSegmentIndex];
      console.log(`📊 Segment ${currentSegmentIndex} data:`, {
        text: segment?.segment_text?.substring(0, 50) + '...',
        start: segment?.start_time,
        end: segment?.end_time,
        audio_url: segment?.audio_url ? 'Present' : 'None'
      });

      if (segment) {
        setCurrentSegment({
          text: segment.segment_text,
          start: segment.start_time,
          end: segment.end_time,
          audio_url: segment.audio_url,
        });

        if (currentSegmentIndex > 0) {
          const delayTime = isMobile ? 201 : 200;
          const timeoutId = window.setTimeout(() => {
            console.log(`▶️ Playing segment ${currentSegmentIndex}`);
            segmentHasPlayedRef.current = false;
            playSegmentAudio(segment);
          }, delayTime);
          return () => clearTimeout(timeoutId);
        }
      }
    }
  }, [showFinalText, currentSegmentIndex]);

  // Special handling for cleanup when showing the final text
  useEffect(() => {
    if (showFinalText && isRecording) {
      console.log('Final text shown while recording was active - forcing cleanup');
      stopRecording();
      cleanupSpeech();
    }
  }, [showFinalText, isRecording, stopRecording, cleanupSpeech]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="flex flex-col items-center bg-white p-8 rounded-lg shadow-md">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500 mb-4"></div>
          <p className="text-gray-600">Loading lesson content...</p>
          {loadingError && (
            <p className="text-red-500 mt-4">{loadingError}</p>
          )}
        </div>
      </div>
    );
  }

  if (showFinalText) {
    return (
      <>
      <div className="max-w-3xl mx-auto p-6">
        <div className="bg-white rounded-lg shadow-lg p-4 sm:p-8">
          <h2 className="text-2xl font-bold text-gray-900">{t('Complete Lesson Text')}</h2>

          <AudioControls
            isPlaying={isCurrentlyPlaying}
            currentTime={(() => {
              if (!audioSegments[currentSegmentIndex]?.audio_url) return currentTime;
              const accumulated = segmentDurations.slice(0, currentSegmentIndex).reduce((a, b) => a + (b || 0), 0);
              return accumulated + segmentCurrentTime;
            })()}
            duration={(() => {
              if (!audioSegments[currentSegmentIndex]?.audio_url) return duration;
              return segmentDurations.reduce((a, b) => a + (b || 0), 0) || segmentDuration;
            })()}
            onPlay={(start, end) => {
              // Stop any current playback first
              pauseSegmentAudio();

              // For AI-generated lessons, find and play the current segment
              if (audioSegments[currentSegmentIndex]?.audio_url) {
                setTimeout(() => {
                  playSegmentAudio(audioSegments[currentSegmentIndex]);
                }, 100);
              } else {
                // For traditional lessons with timestamps
                const index = audioSegments.findIndex(segment =>
                  Math.abs(segment.start_time - start) < 0.1
                );
                if (index >= 0) {
                  setCurrentSegmentIndex(index);
                }
                wasPlayingRef.current = false;
                setTimeout(() => {
                  playAudio(start, end);
                }, 100);
              }
            }}
            onPause={pauseSegmentAudio}
            audioSegment={currentSegment}
          />

          <div
            ref={scrollContainerRef}
            className="space-y-4 max-h-[50vh] sm:max-h-[60vh] overflow-y-auto py-3 sm:py-4 px-2 scroll-smooth"
          >
            {(() => {
              // Re-split both original text and translation using the updated splitIntoSentences function
              // This ensures consistent sentence detection regardless of how segments were originally stored
              const allOriginalSegments = splitIntoSentences(lessonText);
              const allTranslations = splitIntoSentences(lessonTranslation);

              return audioSegments.map((segment, index) => {
                // Use the properly split text instead of the database segment text
                const segmentText = allOriginalSegments[index] || segment.segment_text.trim();
                const segmentTranslation = allTranslations[index] || 'Translation not available';

                return (
                  <div
                    ref={el => segmentRefs.current[index] = el}
                    key={index}
                    className={`p-6 rounded-xl transition-all duration-300 ${
                      currentSegmentIndex === index
                        ? 'bg-indigo-50 border-2 border-indigo-200 shadow-md'
                        : 'bg-gray-50 hover:bg-gray-100'
                    }`}
                    onClick={() => {
                      segmentHasPlayedRef.current = false;
                      setCurrentSegmentIndex(index);
                      pauseSegmentAudio();
                    }}
                  >
                    <p className="text-lg sm:text-xl mb-2 sm:mb-3 font-medium">{segmentText}</p>
                    <p className="text-gray-600 italic">{segmentTranslation}</p>
                  </div>
                );
              });
            })()}
          </div>

          <div className="flex justify-end pt-4 sm:pt-6 mt-4 sm:mt-6 border-t">
            <button
              onClick={async () => {
                if (isCompleting) return;

                try {
                  setIsCompleting(true);

                  // Set flag first to prevent further recording attempts
                  navigationRef.current = true;

                  // Stop recording - this calls cleanup internally
                  stopRecording();

                  // Show completion popup immediately for better UX
                  setShowCompletionPopup(true);

                  // Mark as completed in background (non-blocking)
                  markLessonCompleted(false).catch(err => {
                    console.error('Error marking lesson as completed:', err);
                  });
                } catch (error) {
                  console.error('Error completing lesson:', error);
                  setShowCompletionPopup(true);
                } finally {
                  setIsCompleting(false);
                }
              }}
              className={`flex items-center px-8 py-4 bg-green-500 hover:bg-green-600 text-white rounded-full text-lg font-medium transition-all transform hover:scale-105 ${
                currentSegmentIndex < audioSegments.length - 1 || isCompleting
                  ? 'opacity-50 cursor-not-allowed'
                  : 'shadow-lg animate-celebration-bounce'
              }`}
              disabled={currentSegmentIndex < audioSegments.length - 1 || isCompleting}
            >
              {isCompleting ? (
                <>🎉 Completing...</>
              ) : currentSegmentIndex < audioSegments.length - 1 ? (
                'Please listen to the complete lesson'
              ) : (
                'Complete Lesson'
              )}
            </button>
          </div>
        </div>
      </div>

      {showCompletionPopup && (() => {
        console.log('Rendering LessonCompletionPopup with surveyData:', surveyData);
        return (
          <LessonCompletionPopup
            currentLessonTitle={completedLessonTitle}
            currentLevel={user?.currentLevel || 'A1'}
            userId={user?.uid || ''}
            lessonId={lessonId || ''}
            initialUnderstanding={surveyData.initialUnderstanding}
            surveyId={surveyData.surveyId}
            onContinueToNext={(nextLessonId) => {
              setShowCompletionPopup(false);
              navigate(`/lesson/${nextLessonId}`);
            }}
            onGoToLessons={() => {
              setShowCompletionPopup(false);
              navigate('/lessons');
            }}
            nativeLanguage={nativeLanguage}
          />
        );
      })()}
      </>
    );
  }

  if (practiceMode === 'word' && unknownWords.length > 0) {
    console.log('Showing word practice');
    return (
      <WordPractice
        key={`word-practice-${wordBankData.length}-${showWordCards}-${showWordQuiz}`}
        words={unknownWords}
        wordBankData={wordBankData}
        showWordCards={showWordCards}
        showWordQuiz={showWordQuiz}
        shouldShowQuiz={shouldShowQuiz}
        onWordCardComplete={handleWordCardComplete}
        onQuizComplete={() => {
          console.log('Quiz completed, moving to final text');
          setShowWordQuiz(false);
          setPracticeMode('sentence');
          setShowFinalText(true);
          if (audioSegments.length > 0) {
            setCurrentSegmentIndex(0);
            playSegmentAudio(audioSegments[0]);
          }
        }}
      />
    );
  }

  if (currentSentence && practiceMode === 'sentence') {
    console.log('Showing sentence practice');
    return (
      <>
        <SentencePractice
          sentence={currentSentence}
          currentIndex={currentSentenceIndex}
          isInitializing={isInitializing}
          totalSentences={sentences.length}
          isAdmin={isAdmin}
          audioSegment={currentSegment}
          onPlayAudio={playAudio}
          onWordClick={handleWordClick}
          onNext={handleNext}
          isRecording={isRecording}
          isAudioPlaying={isCurrentlyPlaying}
          spokenText={spokenText}
          matchPercentage={matchPercentage}
          attempts={attempts}
          canSkip={canSkip}
          sentenceCompleted={sentenceCompleted}
          onStartRecording={startRecording}
          onStopRecording={stopRecording}
          onReleaseStream={async () => { await releaseStream(); setAudioReadyAt(Date.now()); }}
          unknownWords={unknownWords}
          setSentenceCompleted={setSentenceCompleted}
          nativeLanguage={nativeLanguage}
          audioReadyAt={audioReadyAt}
        />

        <div className="max-w-3xl mx-auto px-6 pb-6">
          <button
            onClick={() => setShowReportModal(true)}
            className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            <Flag className="h-4 w-4" />
            {t('Report Issue')}
          </button>
        </div>

        {showReportModal && lessonId && user && (
          <ReportIssueModal
            lessonId={lessonId}
            lessonTitle={lessonTitle}
            userId={user.uid}
            nativeLanguage={nativeLanguage}
            onClose={() => setShowReportModal(false)}
          />
        )}
      </>
    );
  }

  // Fallback UI if no content is available
  return (
    <div className="flex justify-center items-center h-screen">
      <div className="bg-white p-6 rounded-lg shadow-md max-w-md w-full">
        <h2 className="text-xl font-bold text-gray-900 mb-4">No Content Available</h2>
        <p className="text-gray-600 mb-6">
          {loadingError || "Unable to load lesson content. The lesson may have been deleted or is unavailable."}
        </p>
        <button
          onClick={() => navigate('/lessons')}
          className="w-full px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
        >
          Return to Lessons
        </button>
      </div>
    </div>
  );
}

export { PracticePage };